'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function DewPointCalculator() {
  const t = useTranslations("tools.dewPointCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    airTemp: '',
    airTempUnit: 'celsius',
    humidity: '',
    dewPoint: '',
    dewPointUnit: 'celsius'
  });
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  // Temperature conversion functions
  const toCelsius = (value: number, unit: string): number => {
    if (unit === 'celsius') return value;
    if (unit === 'fahrenheit') return (value - 32) * 5 / 9;
    if (unit === 'kelvin') return value - 273.15;
    return value;
  };

  const fromCelsius = (value: number, unit: string): number => {
    if (unit === 'celsius') return value;
    if (unit === 'fahrenheit') return value * 9 / 5 + 32;
    if (unit === 'kelvin') return value + 273.15;
    return value;
  };

  // Calculation functions
  const calculateDewPoint = (tempC: number, humidity: number): number => {
    const a = 17.27;
    const b = 237.7;
    const alpha = ((a * tempC) / (b + tempC)) + Math.log(humidity / 100);
    const dewPoint = (b * alpha) / (a - alpha);
    return dewPoint;
  };

  const calculateHumidity = (tempC: number, dewPointC: number): number => {
    const a = 17.27;
    const b = 237.7;
    const numerator = Math.exp((a * dewPointC) / (b + dewPointC));
    const denominator = Math.exp((a * tempC) / (b + tempC));
    const humidity = 100 * (numerator / denominator);
    return Math.min(100, Math.max(0, humidity));
  };

  const calculateTemperature = (dewPointC: number, humidity: number): number => {
    const a = 17.27;
    const b = 237.7;
    const alpha = Math.log(humidity / 100) + (a * dewPointC) / (b + dewPointC);
    const temp = (b * alpha) / (a - alpha);
    return temp;
  };

  const calculateAbsoluteHumidity = (tempC: number, humidity: number): number => {
    const es = 6.112 * Math.exp((17.67 * tempC) / (tempC + 243.5));
    const e = (humidity / 100) * es;
    const absoluteHumidity = (2.1674 * e) / (tempC + 273.15);
    return absoluteHumidity;
  };

  const getComfortLevel = (dewPointC: number): string => {
    if (dewPointC < 10) return t('comfortDry');
    if (dewPointC < 16) return t('comfortComfortable');
    if (dewPointC < 21) return t('comfortSticky');
    return t('comfortOppressive');
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResults(null);

    const { airTemp, airTempUnit, humidity, dewPoint, dewPointUnit } = inputs;
    const filledCount = [airTemp, humidity, dewPoint].filter(v => v !== '').length;

    if (filledCount !== 2) {
      setError(t('errorTwoValues'));
      return;
    }

    if (humidity !== '' && (parseFloat(humidity) < 0 || parseFloat(humidity) > 100)) {
      setError(t('errorHumidityRange'));
      return;
    }

    try {
      let airTempC: number, humidityVal: number, dewPointC: number;
      let calculatedField = '';

      if (airTemp === '') {
        dewPointC = toCelsius(parseFloat(dewPoint), dewPointUnit);
        humidityVal = parseFloat(humidity);
        airTempC = calculateTemperature(dewPointC, humidityVal);
        calculatedField = 'temperature';
      } else if (humidity === '') {
        airTempC = toCelsius(parseFloat(airTemp), airTempUnit);
        dewPointC = toCelsius(parseFloat(dewPoint), dewPointUnit);
        humidityVal = calculateHumidity(airTempC, dewPointC);
        calculatedField = 'humidity';
      } else {
        airTempC = toCelsius(parseFloat(airTemp), airTempUnit);
        humidityVal = parseFloat(humidity);
        dewPointC = calculateDewPoint(airTempC, humidityVal);
        calculatedField = 'dewpoint';
      }

      const absoluteHumidity = calculateAbsoluteHumidity(airTempC, humidityVal);
      const airTempDisplay = fromCelsius(airTempC, airTempUnit);
      const dewPointDisplay = fromCelsius(dewPointC, dewPointUnit);
      const comfortLevel = getComfortLevel(dewPointC);

      const tempUnitSymbol = airTempUnit === 'fahrenheit' ? '°F' : airTempUnit === 'celsius' ? '°C' : 'K';
      const dewUnitSymbol = dewPointUnit === 'fahrenheit' ? '°F' : dewPointUnit === 'celsius' ? '°C' : 'K';

      setResults({
        calculatedField,
        airTemp: airTempDisplay,
        airTempUnit: tempUnitSymbol,
        humidity: humidityVal,
        dewPoint: dewPointDisplay,
        dewPointUnit: dewUnitSymbol,
        absoluteHumidity,
        comfortLevel
      });
    } catch (err) {
      console.error(err);
      setError(t('errorCalculation'));
    }
  };

  const handleClear = () => {
    setInputs({
      airTemp: '',
      airTempUnit: 'celsius',
      humidity: '',
      dewPoint: '',
      dewPointUnit: 'celsius'
    });
    setResults(null);
    setError('');
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #e0f2fe 0%, #dbeafe 50%, #e0e7ff 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem' },
    { padding: '3rem 1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    {
      margin: '0 auto',
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden' as const
    },
    { maxWidth: '100%' },
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = {
    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
    color: 'white',
    padding: '2rem',
    textAlign: 'center' as const
  };

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.5rem' },
    { fontSize: '1.6rem' },
    { fontSize: '1.8rem' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const infoTextStyle = {
    background: '#f1f5f9',
    padding: '1rem',
    borderRadius: '0.5rem',
    fontSize: '0.9rem',
    color: '#475569',
    marginBottom: '1.5rem',
    borderLeft: '4px solid #2563eb'
  };

  const formGridStyle = {
    display: 'grid',
    gap: '1.5rem',
    marginBottom: '2rem'
  };

  const formGroupStyle = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.5rem'
  };

  const labelStyle = {
    fontWeight: 600,
    color: colors.textPrimary,
    fontSize: '0.95rem'
  };

  const inputWrapperStyle = getResponsiveStyle(
    { display: 'flex', gap: '0.75rem', alignItems: 'center' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const inputStyle = {
    flex: 1,
    padding: '0.75rem 1rem',
    border: '2px solid #e2e8f0',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
    width: screenSize === 'mobile' ? '100%' : 'auto'
  };

  const selectStyle = {
    padding: '0.75rem 1rem',
    border: '2px solid #e2e8f0',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    background: 'white',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    minWidth: screenSize === 'mobile' ? '100%' : '140px'
  };

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', justifyContent: 'center' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)',
      fontSize: '1rem'
    },
    { padding: '0.875rem 2rem', width: '100%' },
    { padding: '0.875rem 2.5rem', width: 'auto' },
    { padding: '0.875rem 2.5rem', width: 'auto' }
  );

  const clearButtonStyle = getResponsiveStyle(
    {
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569',
      fontSize: '1rem'
    },
    { padding: '0.875rem 2rem', width: '100%' },
    { padding: '0.875rem 2.5rem', width: 'auto' },
    { padding: '0.875rem 2.5rem', width: 'auto' }
  );

  const resultsStyle = {
    marginTop: '2rem',
    padding: '2rem',
    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
    borderRadius: '0.75rem',
    border: '2px solid #bae6fd'
  };

  const resultsH3Style = {
    margin: '0 0 1.5rem 0',
    color: '#0c4a6e',
    fontSize: '1.3rem',
    textAlign: 'center' as const
  };

  const resultGridStyle = {
    display: 'grid',
    gap: '1rem'
  };

  const resultItemStyle = {
    background: 'white',
    padding: '1rem 1.5rem',
    borderRadius: '0.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    flexWrap: 'wrap' as const,
    gap: '0.5rem'
  };

  const resultPrimaryStyle = {
    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
    color: 'white',
    padding: '1.25rem 1.5rem'
  };

  const resultLabelStyle = {
    fontWeight: 600,
    color: '#475569'
  };

  const resultValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#2563eb' },
    { fontSize: '1.1rem' },
    { fontSize: '1.15rem' },
    { fontSize: '1.2rem' }
  );

  const errorMessageStyle = {
    background: '#fee2e2',
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
    borderLeft: '4px solid #dc2626'
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = {
    ...paragraphStyle,
    paddingLeft: '1.5rem'
  };

  const listItemStyle = {
    marginBottom: '0.75rem'
  };

  const strongStyle = {
    color: colors.textPrimary,
    fontWeight: 600
  };

  const refLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    borderBottom: '1px solid transparent',
    transition: 'border-color 0.3s ease'
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>💧 {t('toolTitle')}</h2>
          </div>

          <form style={calculatorFormStyle} onSubmit={handleCalculate}>
            <div style={infoTextStyle}>
              <strong>{t('instructionsLabel')}</strong> {t('instructionsText')}
            </div>

            <div style={formGridStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="air-temp">{t('airTempLabel')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    style={inputStyle}
                    type="number"
                    id="air-temp"
                    step="0.1"
                    placeholder={t('airTempPlaceholder')}
                    value={inputs.airTemp}
                    onChange={(e) => setInputs({...inputs, airTemp: e.target.value})}
                  />
                  <select
                    style={selectStyle}
                    id="air-temp-unit"
                    value={inputs.airTempUnit}
                    onChange={(e) => setInputs({...inputs, airTempUnit: e.target.value})}
                  >
                    <option value="fahrenheit">{t('unitFahrenheit')}</option>
                    <option value="celsius">{t('unitCelsius')}</option>
                    <option value="kelvin">{t('unitKelvin')}</option>
                  </select>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="humidity">{t('humidityLabel')}</label>
                <input
                  style={{...inputStyle, width: '100%'}}
                  type="number"
                  id="humidity"
                  step="0.1"
                  min="0"
                  max="100"
                  placeholder={t('humidityPlaceholder')}
                  value={inputs.humidity}
                  onChange={(e) => setInputs({...inputs, humidity: e.target.value})}
                />
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="dew-point">{t('dewPointLabel')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    style={inputStyle}
                    type="number"
                    id="dew-point"
                    step="0.1"
                    placeholder={t('dewPointPlaceholder')}
                    value={inputs.dewPoint}
                    onChange={(e) => setInputs({...inputs, dewPoint: e.target.value})}
                  />
                  <select
                    style={selectStyle}
                    id="dew-point-unit"
                    value={inputs.dewPointUnit}
                    onChange={(e) => setInputs({...inputs, dewPointUnit: e.target.value})}
                  >
                    <option value="fahrenheit">{t('unitFahrenheit')}</option>
                    <option value="celsius">{t('unitCelsius')}</option>
                    <option value="kelvin">{t('unitKelvin')}</option>
                  </select>
                </div>
              </div>
            </div>

            <div style={buttonGroupStyle}>
              <button type="submit" style={calculateButtonStyle}>{t('calculateButton')}</button>
              <button type="button" style={clearButtonStyle} onClick={handleClear}>{t('clearButton')}</button>
            </div>

            {error && (
              <div style={errorMessageStyle}>{error}</div>
            )}
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>📊 {t('resultsTitle')}</h3>
              <div style={resultGridStyle}>
                <div style={{...resultItemStyle, ...resultPrimaryStyle}}>
                  <span style={{...resultLabelStyle, color: '#e0e7ff'}}>
                    {results.calculatedField === 'temperature' ? t('airTempLabel') :
                     results.calculatedField === 'humidity' ? t('humidityLabel') :
                     t('dewPointLabel')}
                  </span>
                  <span style={{...resultValueStyle, color: 'white', fontSize: screenSize === 'mobile' ? '1.3rem' : '1.5rem'}}>
                    {results.calculatedField === 'temperature' ? `${results.airTemp.toFixed(2)} ${results.airTempUnit}` :
                     results.calculatedField === 'humidity' ? `${results.humidity.toFixed(2)}%` :
                     `${results.dewPoint.toFixed(2)} ${results.dewPointUnit}`}
                  </span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('airTempLabel')}</span>
                  <span style={resultValueStyle}>{results.airTemp.toFixed(2)} {results.airTempUnit}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('humidityLabel')}</span>
                  <span style={resultValueStyle}>{results.humidity.toFixed(2)}%</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('absoluteHumidityLabel')}</span>
                  <span style={resultValueStyle}>{results.absoluteHumidity.toFixed(2)} g/m³</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('comfortLevelLabel')}</span>
                  <span style={resultValueStyle}>{results.comfortLevel}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step1Title')}</strong> {t('step1Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step2Title')}</strong> {t('step2Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step3Title')}</strong> {t('step3Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step4Title')}</strong> {t('step4Text')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insight1Title')}</strong> {t('insight1Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insight2Title')}</strong> {t('insight2Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insight3Title')}</strong> {t('insight3Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insight4Title')}</strong> {t('insight4Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insight5Title')}</strong> {t('insight5Text')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('applicationsTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('app1Title')}</strong> {t('app1Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('app2Title')}</strong> {t('app2Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('app3Title')}</strong> {t('app3Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('app4Title')}</strong> {t('app4Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('app5Title')}</strong> {t('app5Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('app6Title')}</strong> {t('app6Text')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('comfortGuideTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('comfort1Range')}</strong> {t('comfort1Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('comfort2Range')}</strong> {t('comfort2Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('comfort3Range')}</strong> {t('comfort3Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('comfort4Range')}</strong> {t('comfort4Text')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>
        <div>
          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.1rem' : '1.25rem', marginTop: '1.5rem'}}>{t('faq1Question')}</h3>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>

          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.1rem' : '1.25rem', marginTop: '1.5rem'}}>{t('faq2Question')}</h3>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>

          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.1rem' : '1.25rem', marginTop: '1.5rem'}}>{t('faq3Question')}</h3>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>

          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.1rem' : '1.25rem', marginTop: '1.5rem'}}>{t('faq4Question')}</h3>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>

          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.1rem' : '1.25rem', marginTop: '1.5rem'}}>{t('faq5Question')}</h3>
          <p style={paragraphStyle}>{t('faq5Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://www.processsensing.com/en-us/humidity-calculator/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref1')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.omnicalculator.com/physics/dew-point" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref2')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.processsensing.com/en-us/blog/dew-point-definition-and-how-to-measure-it-for-industries.htm" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref3')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.calculator.net/dew-point-calculator.html" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref4')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="http://www.dpcalc.org" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref5')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}